"use client";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CacheKeys } from "../../constants/CacheKeys";
import { VsaTableColumnDefinition } from "../../constants/DataGridColumns";
import { SelectionPersistenceKeys } from "../../constants/SelectionPersistenceKeys";
import { useGetActivityFields } from "../../hooks/useActivities";
import { useGetDocumentFields } from "../../hooks/useTools";
import { ApiEndpoints } from "../../models/ApiEndpoints";
import { ClientRoutes } from "../../models/ClientRoutes";
import { DocumentGridViewItem } from "../../models/DocumentGridViewItem";
import {
  getDocumentGridColumnsFromColumnOrder,
  mapDocumentGridItemIds,
} from "../../services/DocumentGridViewService";
import { ReportType } from "../../services/ReportService";
import { MassCreateDraftButton } from "../list/MassCreateDraftButton";
import { PaginatedList } from "../list/PaginatedList";
import DownloadOriginalDocumentsButton from "./DownloadOriginalDocumentsButton";

/**
 * Published documents list backed by the DocumentGridView API. Field values
 * are pre-merged on the server (document fields plus inherited activity
 * fields), so no client-side merge transformation is required.
 */
const PublishedDocumentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const onMassCreateDraftSuccess = (createdVersionIds: string[]) => {
    window.sessionStorage.setItem(
      SelectionPersistenceKeys.DocumentsDrafts,
      JSON.stringify(createdVersionIds)
    );
    navigate(`${ClientRoutes.Documents}?view=drafts`);
  };

  const { data: documentViewDefinition, isLoading: documentFieldsLoading } =
    useGetDocumentFields();

  const {
    data: activityViewDefinition,
    isLoading: activityViewDefinitionLoading,
  } = useGetActivityFields();

  const columns = useMemo<
    VsaTableColumnDefinition<DocumentGridViewItem>[]
  >(() => {
    if (
      documentFieldsLoading ||
      !documentViewDefinition ||
      activityViewDefinitionLoading ||
      !activityViewDefinition
    ) {
      return [];
    }

    return getDocumentGridColumnsFromColumnOrder(
      documentViewDefinition.standardFields,
      documentViewDefinition.fields,
      activityViewDefinition.fields,
      documentViewDefinition.publishedFieldOrder,
      false,
      { showNewDocumentIndicator: true }
    );
  }, [
    documentViewDefinition,
    documentFieldsLoading,
    activityViewDefinition,
    activityViewDefinitionLoading,
  ]);

  return (
    <PaginatedList<DocumentGridViewItem>
      apiEndpoint={ApiEndpoints.DocumentGridViewCurrent}
      columns={columns}
      defaultSortAscending={false}
      defaultSortColumnName={"modified"}
      searchPlaceholder={t("searchTool")}
      postQueryTransformation={mapDocumentGridItemIds}
      cacheKey={CacheKeys.Document.DocumentList}
      filtersEndpoint={ApiEndpoints.ToolDocumentFilters}
      reportType={ReportType.PublishedDocuments}
      storageKey="documents:published"
      libraryFilterScope={{ listType: "document" }}
      selectedActions={(selectedVersionIds) => (
        <>
          <DownloadOriginalDocumentsButton versionIds={selectedVersionIds} />
          <MassCreateDraftButton
            versionIds={selectedVersionIds}
            entityType="tool"
            onSuccess={onMassCreateDraftSuccess}
          />
        </>
      )}
    />
  );
};

export default PublishedDocumentList;
